import { useEffect, useState } from 'react';
import type { LimitKey, TenantLimit, TenantLimitOverridesInput } from '@neuroedge/shared';
import { Button, Card, PageState, useToast } from '../../components/ui';
import { useMyLimits, useSetTenantLimits, useTenantLimits } from '../../api/billing';
import { fieldErrorsFromApiError } from '../../lib/field-errors';

const muted = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' } as const;
const n = (v: number) => v.toLocaleString('en-IN');
const limitText = (v: number | null) => (v === null ? 'Unlimited' : n(v));

function UsageBar({ used, limit }: { used: number; limit: number | null }) {
  if (limit === null) return null;
  const pct = limit === 0 ? 100 : Math.min((used / limit) * 100, 100);
  const tone = pct >= 100 ? 'var(--color-danger)' : pct >= 80 ? '#d97706' : 'var(--color-primary)';
  return (
    <div
      style={{ height: 6, borderRadius: 3, background: 'var(--color-border)', overflow: 'hidden' }}
      aria-hidden
    >
      <div style={{ height: '100%', width: `${pct}%`, background: tone }} />
    </div>
  );
}

/** Billing page: this workspace's use against its plan limits. */
export function MyLimitsCard() {
  const limits = useMyLimits();
  return (
    <Card title="Usage and limits">
      {limits.isPending ? (
        <PageState kind="loading" />
      ) : limits.isError ? (
        <PageState kind="error" message={limits.error.message} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 560 }}>
          {limits.data.limits.map((l) => (
            <div key={l.key} style={{ display: 'grid', gap: 4 }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-2)' }}
              >
                <span>{l.label}</span>
                <span
                  style={
                    l.limit !== null && l.used >= l.limit
                      ? { color: 'var(--color-danger)' }
                      : undefined
                  }
                >
                  {n(l.used)} / {limitText(l.limit)}
                </span>
              </div>
              <UsageBar used={l.used} limit={l.limit} />
            </div>
          ))}
          <p style={{ margin: 0, ...muted }}>
            New WhatsApp customers are always added; when active contacts reach the limit, the least
            recently active ones are archived automatically. Upgrade your plan for higher limits.
          </p>
        </div>
      )}
    </Card>
  );
}

type Mode = 'plan' | 'unlimited' | 'custom';
interface Row {
  mode: Mode;
  value: string;
}

const toRow = (l: TenantLimit): Row =>
  l.override === null
    ? { mode: 'plan', value: '' }
    : l.override.value === null
      ? { mode: 'unlimited', value: '' }
      : { mode: 'custom', value: String(l.override.value) };

/**
 * Admin → Tenants → Billing: each limit's plan value and use, and an override
 * for this workspace (follow the plan / unlimited / a number).
 */
export function TenantLimitsPanel({ tenantId }: { tenantId: string }) {
  const limits = useTenantLimits(tenantId);
  const save = useSetTenantLimits(tenantId);
  const toast = useToast();
  const [rows, setRows] = useState<Record<string, Row> | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (limits.data) {
      setRows(Object.fromEntries(limits.data.limits.map((l) => [l.key, toRow(l)])));
    }
  }, [limits.data]);

  if (limits.isPending || !rows) return <PageState kind="loading" />;
  if (limits.isError) return <PageState kind="error" message={limits.error.message} />;

  const set = (key: string, patch: Partial<Row>) => {
    setRows((r) => ({ ...r!, [key]: { ...r![key]!, ...patch } }));
    setErrors((e) => {
      const next = { ...e };
      delete next[key];
      return next;
    });
  };

  async function onSave() {
    const next: Record<string, string> = {};
    const overrides: TenantLimitOverridesInput = {};
    for (const l of limits.data!.limits) {
      const row = rows![l.key]!;
      if (row.mode === 'plan') overrides[l.key as LimitKey] = null;
      else if (row.mode === 'unlimited') overrides[l.key as LimitKey] = { value: null };
      else if (!/^\d+$/.test(row.value.trim())) next[l.key] = 'Enter a whole number (0 or more).';
      else overrides[l.key as LimitKey] = { value: Number(row.value) };
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    try {
      await save.mutateAsync(overrides);
      toast.success('Limits saved.');
    } catch (err) {
      const fields = fieldErrorsFromApiError(err);
      setErrors(
        Object.fromEntries(
          Object.entries(fields).map(([k, v]) => [k.replace(/^overrides\//, '').split('/')[0]!, v]),
        ),
      );
      toast.error(err instanceof Error ? err.message : 'Could not save the limits.');
    }
  }

  return (
    <div>
      <h3 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--font-size-base)' }}>
        Limits{' '}
        {limits.data.planName ? <span style={muted}>· plan {limits.data.planName}</span> : null}
      </h3>
      <div style={{ overflowX: 'auto' }}>
        <table className="ne-table" style={{ minWidth: 620 }}>
          <thead>
            <tr>
              <th>Limit</th>
              <th style={{ textAlign: 'right' }}>Used</th>
              <th style={{ textAlign: 'right' }}>Plan</th>
              <th>For this workspace</th>
            </tr>
          </thead>
          <tbody>
            {limits.data.limits.map((l) => {
              const row = rows[l.key]!;
              return (
                <tr key={l.key}>
                  <td>{l.label}</td>
                  <td style={{ textAlign: 'right' }}>{n(l.used)}</td>
                  <td style={{ textAlign: 'right' }}>{limitText(l.planValue)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                      <select
                        className="ne-input"
                        style={{ width: 'auto' }}
                        aria-label={`${l.label} limit`}
                        value={row.mode}
                        onChange={(e) => set(l.key, { mode: e.target.value as Mode })}
                      >
                        <option value="plan">Follow plan</option>
                        <option value="unlimited">Unlimited</option>
                        <option value="custom">Custom</option>
                      </select>
                      {row.mode === 'custom' ? (
                        <input
                          className="ne-input"
                          style={{ maxWidth: 120 }}
                          inputMode="numeric"
                          aria-label={`${l.label} custom limit`}
                          value={row.value}
                          onChange={(e) => set(l.key, { value: e.target.value })}
                        />
                      ) : null}
                    </div>
                    {errors[l.key] ? (
                      <span className="ne-field__error" style={{ display: 'block' }}>
                        {errors[l.key]}
                      </span>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 'var(--space-2)' }}>
        <Button type="button" disabled={save.isPending} onClick={() => void onSave()}>
          {save.isPending ? 'Saving…' : 'Save limits'}
        </Button>
      </div>
    </div>
  );
}
